/**
 * Grand Quack Auto — Vibe Jam 2026 portal system.
 *
 * Implements the official vibej.am portal webring spec:
 * - Exit portal (green): always present, redirects to vibej.am/portal/2026
 * - Start portal (red): appears when ?portal=true, returns to ?ref= game
 * - Forwards all query params for continuity between games
 * - "VIBEVERSE PORTAL" label + particle effects per official sample
 *
 * Based on: gist.github.com/levelsio/ffdbfe356b421b97a31664ded4bc961d
 */

import * as THREE from 'three';
import type { System, World } from '../types';

// ── Config (scaled for our 100x100 city) ──────────────────────────────────

const PORTAL_RADIUS = 3;
const TORUS_TUBE = 0.3;
const PARTICLE_COUNT = 300;
const ENTER_DIST = 3.5;
const EXIT_POS = new THREE.Vector3(-40, 0, -40);  // far city corner
const START_POS = new THREE.Vector3(40, 0, 40);    // opposite corner
const GRACE_SECONDS = 8;

// ── URL params ────────────────────────────────────────────────────────────

const urlParams = new URLSearchParams(window.location.search);
const cameFromPortal = !!urlParams.get('portal');
const referrer = urlParams.get('ref') ?? '';

export const isPortalEntry = cameFromPortal;

// ── State ─────────────────────────────────────────────────────────────────

let exitGroup: THREE.Group | null = null;
let startGroup: THREE.Group | null = null;
let exitParticles: THREE.BufferGeometry | null = null;
let startParticles: THREE.BufferGeometry | null = null;
let initialized = false;
let startTime = 0;

// ── Portal builder (matches official sample structure) ────────────────────

function buildPortal(
  world: World, pos: THREE.Vector3, color: number, labelText: string,
): { group: THREE.Group; particles: THREE.BufferGeometry } {
  const group = new THREE.Group();
  group.position.copy(pos);

  // Torus ring (emissive glow).
  const torus = new THREE.Mesh(
    new THREE.TorusGeometry(PORTAL_RADIUS, TORUS_TUBE, 16, 48),
    new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.8 }),
  );
  torus.position.y = PORTAL_RADIUS + 0.5;
  group.add(torus);

  // Inner disc.
  const disc = new THREE.Mesh(
    new THREE.CircleGeometry(PORTAL_RADIUS * 0.85, 32),
    new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.3, side: THREE.DoubleSide }),
  );
  disc.position.y = PORTAL_RADIUS + 0.5;
  group.add(disc);

  // Text label (canvas → texture → plane, per official sample).
  const canvas = document.createElement('canvas');
  canvas.width = 512; canvas.height = 64;
  const ctx2d = canvas.getContext('2d')!;
  ctx2d.fillStyle = `#${color.toString(16).padStart(6, '0')}`;
  ctx2d.font = 'bold 28px Arial';
  ctx2d.textAlign = 'center';
  ctx2d.fillText(labelText, 256, 40);
  const label = new THREE.Mesh(
    new THREE.PlaneGeometry(6, 1),
    new THREE.MeshBasicMaterial({ map: new THREE.CanvasTexture(canvas), transparent: true, side: THREE.DoubleSide }),
  );
  label.position.y = PORTAL_RADIUS * 2 + 1.5;
  group.add(label);

  // Particle ring (per official sample — swirling dots around the torus).
  const geo = new THREE.BufferGeometry();
  const positions = new Float32Array(PARTICLE_COUNT * 3);
  const colors = new Float32Array(PARTICLE_COUNT * 3);
  const r = (color >> 16 & 0xff) / 255;
  const g = (color >> 8 & 0xff) / 255;
  const b = (color & 0xff) / 255;

  for (let i = 0; i < PARTICLE_COUNT * 3; i += 3) {
    const angle = Math.random() * Math.PI * 2;
    const radius = PORTAL_RADIUS + (Math.random() - 0.5) * 1.5;
    positions[i] = Math.cos(angle) * radius;
    positions[i + 1] = Math.sin(angle) * radius + PORTAL_RADIUS + 0.5;
    positions[i + 2] = (Math.random() - 0.5) * 1.5;
    colors[i] = r * (0.8 + Math.random() * 0.2);
    colors[i + 1] = g * (0.8 + Math.random() * 0.2);
    colors[i + 2] = b * (0.8 + Math.random() * 0.2);
  }
  geo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  geo.setAttribute('color', new THREE.BufferAttribute(colors, 3));
  const points = new THREE.Points(geo, new THREE.PointsMaterial({
    size: 0.15, vertexColors: true, transparent: true, opacity: 0.6,
  }));
  group.add(points);

  // Point light beacon.
  const light = new THREE.PointLight(color, 3, 20);
  light.position.y = PORTAL_RADIUS + 0.5;
  group.add(light);

  world.scene.add(group);
  return { group, particles: geo };
}

// ── Navigation ────────────────────────────────────────────────────────────

function buildExitUrl(): string {
  const duckName = localStorage.getItem('gqa-duck-name') ?? 'QuackDuck';
  const p = new URLSearchParams();
  p.set('portal', 'true');
  p.set('username', duckName);
  p.set('color', 'FFD700');
  p.set('speed', '5');
  p.set('ref', window.location.origin + window.location.pathname);
  return `https://vibej.am/portal/2026?${p.toString()}`;
}

function buildReturnUrl(): string {
  let url = referrer;
  if (!url.startsWith('http')) url = 'https://' + url;
  // Forward all params except ref.
  const p = new URLSearchParams();
  for (const [k, v] of urlParams) {
    if (k !== 'ref') p.set(k, v);
  }
  const qs = p.toString();
  return url + (qs ? '?' + qs : '');
}

let preloaded = false;
function preloadExit(): void {
  if (preloaded || document.getElementById('preloadFrame')) return;
  preloaded = true;
  const iframe = document.createElement('iframe');
  iframe.id = 'preloadFrame';
  iframe.style.display = 'none';
  iframe.src = buildExitUrl();
  document.body.appendChild(iframe);
}

// ── System ────────────────────────────────────────────────────────────────

export const portalSystem: System = {
  name: 'portal',

  fixedUpdate(_dt: number, world: World) {
    if (!initialized) {
      initialized = true;
      startTime = world.time.elapsed;
      const exit = buildPortal(world, EXIT_POS, 0x00ff00, 'VIBEVERSE PORTAL');
      exitGroup = exit.group; exitParticles = exit.particles;
      if (cameFromPortal && referrer) {
        const start = buildPortal(world, START_POS, 0xff0000, 'RETURN PORTAL');
        startGroup = start.group; startParticles = start.particles;
      }
    }

    if (world.time.elapsed - startTime < GRACE_SECONDS) return;

    const pos = world.playerMode === 'driving' && world.activeVehicle
      ? world.activeVehicle.group.position : world.duck.position;

    // Exit portal → next game.
    if (exitGroup && pos.distanceTo(EXIT_POS) < ENTER_DIST) {
      window.location.href = buildExitUrl();
    }

    // Preload when near exit.
    if (exitGroup && pos.distanceTo(EXIT_POS) < ENTER_DIST * 4) {
      preloadExit();
    }

    // Start portal → return to previous game.
    if (startGroup && pos.distanceTo(START_POS) < ENTER_DIST) {
      window.location.href = buildReturnUrl();
    }
  },

  render(_dt: number, _alpha: number, _world: World) {
    const t = Date.now() * 0.001;

    // Animate particles (per official sample — sine wave oscillation).
    for (const geo of [exitParticles, startParticles]) {
      if (!geo) continue;
      const posAttr = geo.getAttribute('position') as THREE.BufferAttribute | null;
      if (!posAttr) continue;
      for (let i = 0; i < posAttr.count; i++) {
        posAttr.setY(i, posAttr.getY(i) + 0.03 * Math.sin(t + i));
      }
      posAttr.needsUpdate = true;
    }

    // Rotate torus rings.
    if (exitGroup) {
      (exitGroup.children[0] as THREE.Mesh).rotation.z = t * 0.3;
    }
    if (startGroup) {
      (startGroup.children[0] as THREE.Mesh).rotation.z = -t * 0.3;
    }
  },
};
